import { Assert } from './assert';
import { XPath, XPathUtils } from './xpath-utils';

export interface IAttribute {
  qualifiedName: string;
  value: string;
}

export class Dom {
  get documentElement(): Element {
    return this.doc.documentElement;
  }

  get outerHTML(): string {
    return this.doc.documentElement.outerHTML;
  }
  private doc: Document;

  constructor(xml: string) {
    const xmlParser = new DOMParser();
    this.doc = xmlParser.parseFromString(xml, 'text/xml');
  }

  static findChildByTagName(parent: Element, childTag: string): Element | null {
    for (let i = 0; i < parent.children.length; i++) {
      if (parent.children[i].tagName === childTag) {
        return parent.children[i];
      }
    }
    return null;
  }

  createElement(tagName: string, attributes?: IAttribute[]): Element {
    const element = this.doc.createElement(tagName);
    if (attributes) {
      attributes.forEach((attr) => {
        element.setAttribute(attr.qualifiedName, attr.value);
      });
    }
    return element;
  }

  tryGetChildFromPath(xpath: XPath): Element | undefined {
    try {
      return this.getChildFromPath(xpath);
    } catch {
      return undefined;
    }
  }

  getChildFromPath(xpath: XPath): Element {
    Assert.ok(XPathUtils.isAbsolute(xpath), 'Empty or relative path is not allowed');
    const path = XPathUtils.toArray(xpath);
    Assert.ok(
      path?.length > 0 && path?.[0] === this.doc.documentElement.tagName,
      `The path ${xpath} does not start with root element ${this.doc.documentElement.tagName}`
    );

    let parent: Element = this.doc.documentElement; // assumes root namespace element is present (e.g. <bullhorn>)
    let child: Element | null = null;
    for (let i = 1; i < path.length; i++) {
      // assumes first part of path is namespace
      child = Dom.findChildByTagName(parent, path[i]);
      if (!child) {
        break;
      }
      parent = child;
    }

    if (!child) {
      throw new Error(`Child with path '${path.join('/')}' not found.`);
    }

    return child;
  }

  findChildById(id: string): Element | null {
    const parent = this.doc.documentElement;
    for (let i = 0; i < parent.children.length; i++) {
      if (parent.children[i].getAttribute('id') === id) {
        return parent.children[i];
      }
    }
    return null;
  }

  findDescendantsByAttribute(name: string, value: string): Element[] {
    const queue: Element[] = [this.doc.documentElement];
    const results: Element[] = [];

    while (queue.length > 0) {
      const elem = queue.shift()!;

      if (elem.getAttribute(name) === value) {
        results.push(elem);
      }

      for (let i = 0; i < elem.children.length; i++) {
        queue.push(elem.children[i]);
      }
    }

    return results;
  }
}
